import '@ghq-abi/design-system/css';
import '@ghq-abi/design-system-v2/css';
import 'core-js/actual';

import * as React from 'react';
import type { AppProps } from 'next/app';
import NextNProgress from 'nextjs-progressbar';
import { AuthSession } from '@ghq-abi/auth-client-lib';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import { DesignSystemProvider, theme } from '@ghq-abi/design-system';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FormatIcu } from '@tolgee/format-icu';
import {
  BackendFetch,
  DevTools,
  Tolgee,
  TolgeeProvider,
  useTolgeeSSR,
} from '@tolgee/react';

import { ErrorBoundary } from '~/app/components';
import { AbilityProvider } from '~/app/contexts/AbilityContext';
import { FlagsProvider } from '~/app/contexts/FlagsContext';
import { FullscreenProvider } from '~/app/contexts/FullscreenContext';
import { NavProvider } from '~/app/contexts/NavContext';
import { ToastProvider } from '~/app/contexts/ToastContext';
import { UserProvider } from '~/app/contexts/UserContext';
import { globalStyles } from '~/app/styles/global';
import { AppTemplate } from '~/app/templates/App';
import { AppAbility, createUserAbility } from '~/shared/auth/permissions';
import { LcmUser } from '~/shared/auth/types/next-auth';
import { NPS } from '~/shared/components/Metrics';
import { useInitMixPanel } from '~/shared/hooks/useInitMixPanel';
import {
  GA,
  initializeAxeCore,
  queryClientConfig,
  useDatadog,
  useSetupClarityTracking,
} from '~/shared/lib';
import { getEnv, isClient, isLocal } from '~/shared/utils/environments';

import '../app/globals.css';

if (isClient() && isLocal()) {
  initializeAxeCore();
}

globalStyles();

const appEnv = getEnv();

type CustomAppProps = {
  Component: AppProps['Component'] & { unauth?: boolean };
  pageProps: AppProps['pageProps'] & {
    session: AuthSession<LcmUser | null>;
    dehydratedState?: unknown;
    abilityRules?: AppAbility['_indexedRules'];
  };
};

const tolgee = Tolgee()
  .use(BackendFetch())
  .use(DevTools())
  .use(FormatIcu())
  .init({
    defaultLanguage: 'en-US',
    defaultNs: 'default',
    apiKey: process.env.NEXT_PUBLIC_TOLGEE_API_KEY,
    apiUrl: process.env.NEXT_PUBLIC_TOLGEE_API_URL,
  });
export default function App({
  Component,
  pageProps: { session, messages, abilityRules, flags, ...pageProps },
}: CustomAppProps) {
  // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
  const [queryClient] = React.useState(
    () => new QueryClient(queryClientConfig)
  );

  const locale = session?.user?.preferredLanguage ?? 'en-US';

  const ssrTolgee = useTolgeeSSR(tolgee, locale);

  useDatadog(locale, session);
  useSetupClarityTracking(locale, session);
  useInitMixPanel(session);

  return (
    <>
      <TolgeeProvider tolgee={ssrTolgee}>
        <ErrorBoundary>
          <AuthClientProvider session={session} refetchOnWindowFocus={false}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider>
                <DesignSystemProvider>
                  {Component.unauth ? (
                    <Component {...pageProps} />
                  ) : (
                    <UserProvider session={session}>
                      <ToastProvider>
                        <AbilityProvider
                          ability={createUserAbility(
                            session?.user,
                            abilityRules
                          )}
                        >
                          <FullscreenProvider
                            startState={pageProps.startFullscreenState}
                          >
                            <FlagsProvider flags={flags}>
                              <NavProvider>
                                <Hydrate state={pageProps.dehydratedState}>
                                  <AppTemplate session={session}>
                                    <Component {...pageProps} />
                                  </AppTemplate>
                                </Hydrate>
                                <NextNProgress
                                  color={theme.colors.black.toString()}
                                  startPosition={0.3}
                                  stopDelayMs={200}
                                  height={3}
                                />
                              </NavProvider>
                            </FlagsProvider>
                          </FullscreenProvider>
                        </AbilityProvider>
                      </ToastProvider>
                    </UserProvider>
                  )}
                </DesignSystemProvider>
              </ThemeProvider>
              <ReactQueryDevtools />
            </QueryClientProvider>
          </AuthClientProvider>
          {['production', 'staging', 'development'].includes(appEnv) && (
            <NPS.Script
              globalId={session ? session?.user?.globalId : null}
              preferredLanguage={locale}
            />
          )}
        </ErrorBoundary>
      </TolgeeProvider>
      {['production'].includes(appEnv) ? <GA.Script session={session} /> : null}
    </>
  );
}
